import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sparkline from '../components/Sparkline';
import { fetchMp, PARTY_COLORS, PARTY_NAMES, fmtMoney } from '../api';
import { ArrowLeft, ExternalLink } from 'lucide-react';

export default function MPProfile() {
  const { slug } = useParams();
  const [mp, setMp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchMp(slug)
      .then(d => { setMp(d); setLoading(false); })
      .catch(e => { setErr(e); setLoading(false); });
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen bg-cream-100">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-serif text-5xl text-ink-800">Parliamentarian not found</h1>
          <Link to="/" className="mt-6 inline-block text-copper-600">← Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading || !mp) {
    return (
      <div className="min-h-screen bg-cream-100">
        <Header />
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 animate-pulse">
          <div className="h-3 w-24 bg-ink-200 mb-6" />
          <div className="h-14 w-96 bg-ink-200 mb-3" />
          <div className="h-4 w-44 bg-ink-200 mb-12" />
          <div className="grid grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-ink-200 rounded-sm" />)}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const trendValues = (mp.trend || []).map(t => t.total).filter(v => v > 0);
  const max = (mp.breakdown || []).reduce((m, b) => Math.max(m, b.amount), 0) || 1;

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <Link to="/latest-quarter" className="inline-flex items-center gap-1.5 text-[13px] text-ink-500 hover:text-copper-600 mb-8 transition">
          <ArrowLeft size={14} /> Latest quarter
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-b border-ink-300/60 pb-12">
          <div className="lg:col-span-7">
            <div className="kicker text-copper-600 mb-4">Parliamentarian</div>
            <h1 className="font-serif text-[60px] md:text-[72px] leading-[0.98] tracking-tight text-ink-800">{mp.name}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px] text-ink-700">
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: PARTY_COLORS[mp.party] }} />
                <span className="font-semibold">{PARTY_NAMES[mp.party] || mp.party}</span>
              </span>
              {mp.constituency && <><span className="text-ink-300">·</span><span>{mp.constituency}</span></>}
              {mp.province_full && <><span className="text-ink-300">·</span><span>{mp.province_full}</span></>}
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-ink-300/60">
            <div className="kicker text-ink-500 mb-3">Quarter</div>
            <div className="font-serif text-[22px] text-ink-800">{mp.quarter_label || '—'}</div>
            <a href={mp.source_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-copper-600 hover:text-copper-500 transition">
              Source: House of Commons of Canada <ExternalLink size={12} />
            </a>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          <StatCard label="Claimed this quarter" value={fmtMoney(mp.total)} sub={mp.quarter_label} />
          <StatCard label="Of which discretionary" value={fmtMoney(mp.discretionary)} sub="travel + hospitality + contracts" />
          <StatCard label="Largest discretionary line" value={mp.line} sub={fmtMoney(mp.line_amount)} small />
        </div>

        {trendValues.length > 1 && (
          <div className="mt-16">
            <div className="kicker text-copper-600 mb-5">8-quarter trend</div>
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-10">
              <Sparkline data={trendValues} width={1080} height={180} stroke="#C8894A" />
              <div className="mt-4 flex justify-between text-[11px] uppercase tracking-wider text-ink-500">
                {(mp.trend || []).filter(t => t.total > 0).map(t => (
                  <span key={t.quarter_key}>{t.quarter_label}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {mp.breakdown && mp.breakdown.length > 0 && (
          <div className="mt-16">
            <div className="kicker text-copper-600 mb-5">Breakdown by category</div>
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm overflow-hidden">
              {mp.breakdown.map((b, i) => {
                const pct = max ? (b.amount / max) * 100 : 0;
                return (
                  <div key={i} className="grid grid-cols-[1.5fr_3fr_1fr] items-center px-6 py-5 border-b border-ink-300/40 last:border-0">
                    <div className="text-[14px] text-ink-700 font-medium">{b.label}</div>
                    <div className="h-2.5 bg-ink-200 rounded-full overflow-hidden">
                      <div className="h-full bg-copper-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-right font-serif text-[18px] text-ink-800 tabular">{fmtMoney(b.amount)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12 text-[13px] text-ink-500 leading-relaxed max-w-3xl">
          Figures are gross claimed and trace back to the House of Commons of Canada — Members' Expenditures Report,
          published quarterly under the Board of Internal Economy.
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, sub, small }) {
  return (
    <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-7">
      <div className="kicker text-ink-500 mb-4">{label}</div>
      <div className={`font-serif tracking-tight text-ink-800 tabular ${small ? 'text-[24px]' : 'text-[36px]'}`}>{value}</div>
      {sub && <div className="mt-2 text-[13px] text-ink-500">{sub}</div>}
    </div>
  );
}
